// ============================================================
// D2/D3：AI 建模面板（文生3D / 图生3D / AI贴图 / 材质包）
// - 生成：POST /model-gen/jobs → 轮询（含等待进度/排队诊断/取消）→ 产物预览
// - 采纳到场景（GLB → models，AI 标记）/ 应用到选中模型（贴图主色，可撤销）
// - 2026-10-11：可用性横幅、问标（自动重拓扑等）、图生3D 与 AI贴图支持本地上传（可多张）
// ============================================================
import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Drawer, Input, Progress, Segmented, Space, Tag, Tooltip, Typography, Upload, message } from 'antd';
import { DeploymentUnitOutlined, InfoCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { commandBus } from '../../core/commandBus';
import useSceneStore from '../../stores/sceneStore';
import {
  adoptModelGenJob,
  applyModelGenTexture,
  cancelModelGenJob,
  createModelGenJob,
  fetchModelGenJob,
  fetchModelGenProviders,
  type ModelGenJob,
  type ModelGenJobType,
} from '../../api/modelGen';
import { uploadDashboardAsset } from '../../api/assets';
import { fetchMaterialPresets, type MaterialPresetItem } from '../../api/renderPresets';
import { markModelsAiGenerated } from '../../utils/aiEditorOps';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

type Mode = 'text' | 'image' | 'texture' | 'material';

/** 问标：悬浮显示「说明 + 使用案例」 */
function HelpTip({ text }: { text: string }) {
  return (
    <Tooltip title={text}>
      <InfoCircleOutlined style={{ color: '#8c8c8c', marginLeft: 4, cursor: 'help' }} />
    </Tooltip>
  );
}

const HELP = {
  text: '文生3D：用一句话描述要生成的模型，AI 生成 GLB 后可采纳到场景。案例：「小型泵站」「配电柜」「工业风机」。',
  image: '图生3D：上传或粘贴参考图（多张时以第一张为主图），AI 依据图片生成 3D 模型。案例：设备实物照片、产品渲染图。',
  texture: 'AI 贴图：为「当前选中模型」生成贴图风格（生成后可一键应用主色）。可附参考照片（多张取第一张）。案例：锈蚀钢板、蓝色金属漆。',
  material: '材质包：平台内置 12 种 PBR 材质，一键应用到选中模型（可撤销），不调用生成服务。',
  autoRetopo:
    '自动重拓扑：生成后自动执行「重拓扑」再加工——减少三角面、优化网格布线，适合需要轻量化或规范布线的模型；会增加生成时间。案例：生成风机后自动降到 Web 可用的面数。',
  reference: '参考照片：上传现场照片/材质照片，作为贴图生成的风格参考（可选，多张取第一张）。',
};

export default function ModelGenPanel() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('text');
  const [prompt, setPrompt] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [refUrls, setRefUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [autoRetopo, setAutoRetopo] = useState(false);
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [job, setJob] = useState<ModelGenJob | null>(null);
  const [applying, setApplying] = useState(false);
  const [materials, setMaterials] = useState<MaterialPresetItem[]>([]);
  const [materialQuery, setMaterialQuery] = useState('');
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [avail, setAvail] = useState<{ enabled: boolean; hasProvider: boolean } | null>(null);
  const pollRef = useRef(false);
  const selectedModelId = useSceneStore((s) => s.selectedModelId);

  const selectedModelName = useSceneStore((s) => s.models.find((m) => m.id === s.selectedModelId)?.name ?? '');

  const loadMaterials = useCallback(async (q?: string) => {
    setMaterialsLoading(true);
    try {
      const r = await fetchMaterialPresets(q);
      setMaterials(r.items ?? []);
    } catch {
      message.error('材质包加载失败');
    } finally {
      setMaterialsLoading(false);
    }
  }, []);

  // 打开时：检查生成服务可用性（避免用户等几十秒才看到"未启动"）
  useEffect(() => {
    if (!open) return;
    fetchModelGenProviders()
      .then((r) => setAvail({ enabled: !!r.enabled, hasProvider: (r.items ?? []).some((p) => p.available) }))
      .catch(() => setAvail({ enabled: false, hasProvider: false }));
  }, [open]);

  useEffect(() => {
    if (open && mode === 'material') void loadMaterials(materialQuery.trim() || undefined);
  }, [open, mode, materialQuery, loadMaterials]);

  useEffect(() => () => {
    pollRef.current = false;
  }, []);

  const uploadImages = async (files: File[], target: 'primary' | 'reference') => {
    setUploading(true);
    try {
      const urls: string[] = [];
      for (const f of files) {
        const asset = await uploadDashboardAsset(f, { assetType: 'image', visibility: 'private' });
        urls.push(asset.url);
      }
      if (target === 'primary') setImageUrls((prev) => [...prev, ...urls].slice(0, 4));
      else setRefUrls((prev) => [...prev, ...urls].slice(0, 4));
      message.success(`已上传 ${urls.length} 张`);
    } catch {
      message.error('图片上传失败');
    } finally {
      setUploading(false);
    }
  };

  /** I2：一键应用材质包到选中模型（命令总线事务，可撤销） */
  const applyMaterial = async (item: MaterialPresetItem) => {
    const modelId = useSceneStore.getState().selectedModelId;
    if (!modelId) {
      message.warning('请先在场景中选中模型');
      return;
    }
    const result = await commandBus.applyBatch(
      [{ type: 'model.update', payload: { id: modelId, patch: { materials: [item.material] } } }],
      { label: `material:${item.key}` },
    );
    if (!result.ok) {
      message.error(result.error ?? '应用失败');
      return;
    }
    markModelsAiGenerated([modelId]);
    message.success(`已应用材质「${item.name}」到 ${selectedModelName || '选中模型'}（可撤销）`);
  };

  const effectiveImageUrl = imageUrl.trim() || imageUrls[0] || '';

  const generate = async () => {
    const scene = useSceneStore.getState();
    if (!scene.currentScene) {
      message.warning('请先打开场景');
      return;
    }
    if ((mode === 'text' || mode === 'texture') && !prompt.trim()) {
      message.warning('请输入描述（prompt）');
      return;
    }
    if (mode === 'image' && !effectiveImageUrl) {
      message.warning('请上传参考图或填写图片地址');
      return;
    }
    setRunning(true);
    setElapsed(0);
    setJob(null);
    pollRef.current = true;
    const ticker = setInterval(() => setElapsed((v) => v + 1), 1000);
    try {
      const created = await createModelGenJob({
        type: mode as ModelGenJobType,
        ...(prompt.trim() ? { prompt: prompt.trim() } : {}),
        ...(mode === 'image' && effectiveImageUrl ? { imageUrl: effectiveImageUrl } : {}),
        ...(mode === 'texture' && refUrls.length > 0 ? { params: { referenceImageUrls: refUrls } } : {}),
        ...(mode !== 'texture' && autoRetopo ? { params: { autoRetopo: true } } : {}),
      });
      let latest: ModelGenJob | null = null;
      for (let i = 0; i < 200 && pollRef.current; i++) {
        await sleep(1500);
        latest = await fetchModelGenJob(created.jobId);
        setJob(latest);
        if (latest.status === 'succeeded' || latest.status === 'failed' || latest.status === 'canceled') break;
      }
      if (latest?.status === 'succeeded') message.success('生成成功');
      else if (latest?.status === 'canceled') message.info('已取消生成');
      else if (latest) message.error(latest.error ?? `生成未成功（${latest.status}）`);
    } catch (e) {
      message.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '生成失败');
    } finally {
      pollRef.current = false;
      clearInterval(ticker);
      setRunning(false);
    }
  };

  const cancel = async () => {
    if (!job) return;
    try {
      await cancelModelGenJob(job.id);
      pollRef.current = false;
      message.info('已请求取消');
    } catch {
      message.warning('取消失败（任务可能已开始执行）');
    }
  };

  const adopt = async () => {
    const sceneId = useSceneStore.getState().currentScene?.id;
    if (!job || !sceneId) return;
    setApplying(true);
    try {
      const r = await adoptModelGenJob(job.id, { sceneId });
      const result = await commandBus.applyBatch(
        [
          {
            type: 'model.add',
            payload: {
              model: {
                id: r.model.id,
                name: r.model.name,
                url: r.model.url,
                format: 'glb',
                position: [0, 0, 0],
                rotation: [0, 0, 0],
                scale: [1, 1, 1],
                visible: true,
                locked: false,
                aiGenerated: true,
              },
            },
          },
        ],
        { label: 'model-gen:adopt' },
      );
      if (!result.ok) {
        message.error(result.error ?? '采纳失败');
        return;
      }
      message.success(`已采纳「${r.model.name}」到场景（可撤销）`);
    } catch (e) {
      message.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '采纳失败');
    } finally {
      setApplying(false);
    }
  };

  const applyTexture = async () => {
    if (!job) return;
    const modelId = useSceneStore.getState().selectedModelId;
    if (!modelId) {
      message.warning('请先在场景中选中要应用贴图的模型');
      return;
    }
    setApplying(true);
    try {
      const r = await applyModelGenTexture(job.id, { modelId });
      const result = await commandBus.applyBatch(
        [
          {
            type: 'model.update',
            payload: {
              id: modelId,
              patch: {
                materials: [{ name: 'AI 贴图', color: r.color, opacity: 1, metalness: 0.1, roughness: 0.7 }],
              },
            },
          },
        ],
        { label: 'model-gen:texture' },
      );
      if (!result.ok) {
        message.error(result.error ?? '应用失败');
        return;
      }
      markModelsAiGenerated([modelId]);
      message.success(`已应用 AI 贴图（主色 ${r.color}，可撤销）`);
    } catch (e) {
      message.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '应用失败');
    } finally {
      setApplying(false);
    }
  };

  const textureArtifact = job?.result?.artifacts.find((a) => a.kind === 'texture' || a.kind === 'png');
  const glbArtifact = job?.result?.artifacts.find((a) => a.kind === 'glb');
  const color = String(textureArtifact?.meta?.color ?? '#cccccc');

  const unavailable = avail !== null && (!avail.enabled || !avail.hasProvider);
  const statusText =
    job?.status === 'queued'
      ? elapsed >= 15
        ? '任务排队中：等待生成服务处理（若长时间无进展，可能是生成 Worker 未启动，请联系管理员）'
        : '已排队，正在等待生成服务接单…'
      : job?.status === 'running'
        ? `生成中…已等待 ${elapsed}s（云端生成通常 30–180 秒，请耐心等待，期间可继续编辑）`
        : '';

  return (
    <>
      <Tooltip title="AI 建模（文生3D / 图生3D / AI贴图；产物可采纳到场景）">
        <Button size="small" icon={<DeploymentUnitOutlined />} onClick={() => setOpen(true)}>
          AI 建模
        </Button>
      </Tooltip>
      <Drawer title="AI 建模" open={open} onClose={() => setOpen(false)} size={440} destroyOnHidden>
        <Space orientation="vertical" style={{ width: '100%' }} size={12}>
          <Segmented
            value={mode}
            onChange={(v) => setMode(v as Mode)}
            options={[
              { label: <Tooltip title={HELP.text}>文生3D</Tooltip>, value: 'text' },
              { label: <Tooltip title={HELP.image}>图生3D</Tooltip>, value: 'image' },
              { label: <Tooltip title={HELP.texture}>AI贴图</Tooltip>, value: 'texture' },
              { label: <Tooltip title={HELP.material}>材质包</Tooltip>, value: 'material' },
            ]}
          />

          {mode !== 'material' && unavailable ? (
            <Alert
              type="warning"
              showIcon
              data-testid="model-gen-unavailable"
              message={avail?.enabled ? '当前无可用生成通道' : '模型生成服务未启用'}
              description={
                avail?.enabled
                  ? '云端 Provider 未配置且本地模拟已关闭；请管理员在推广后台「AI 云 Provider」配置密钥后重试。'
                  : '服务端未开启 MODEL_GEN_ENABLED（请管理员用 start-dtwin.ps1 / 完整 env 启动后重试）。'
              }
            />
          ) : null}

          {mode === 'material' ? (
            <>
              <Input
                placeholder="搜索材质（如：锈蚀 / 混凝土 / 地面）"
                value={materialQuery}
                onChange={(e) => setMaterialQuery(e.target.value)}
                allowClear
              />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                目标模型：{selectedModelName || '（未选中，应用前请先选中模型）'}
              </Typography.Text>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 420, overflow: 'auto' }} data-mg-materials>
                {materials.map((m) => (
                  <div key={m.key} data-material-key={m.key} style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 22, height: 22, background: m.material.color, borderRadius: 4, border: '1px solid #ddd', display: 'inline-block' }} />
                      <span style={{ fontWeight: 600, flex: 1 }}>{m.name}</span>
                      <Button
                        size="small"
                        type="primary"
                        data-mg-action="apply-material"
                        disabled={!selectedModelId}
                        onClick={() => void applyMaterial(m)}
                      >
                        应用
                      </Button>
                    </div>
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>{m.description}</div>
                    <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
                      金属 {m.material.metalness} · 粗糙 {m.material.roughness} · 透明 {m.material.opacity}
                    </div>
                    <div style={{ marginTop: 4 }}>
                      {m.tags.map((t) => (
                        <Tag key={t} style={{ fontSize: 11 }}>
                          {t}
                        </Tag>
                      ))}
                    </div>
                  </div>
                ))}
                {!materialsLoading && materials.length === 0 ? (
                  <div style={{ fontSize: 12, color: '#8c8c8c' }}>未找到匹配材质</div>
                ) : null}
              </div>
            </>
          ) : null}

          {mode !== 'material' ? (
            <>
              {mode !== 'image' ? (
                <Input.TextArea
                  rows={3}
                  placeholder={mode === 'text' ? '描述要生成的模型，如：小型泵站 / 配电柜 / 风机' : '描述贴图风格，如：锈蚀钢板 / 蓝色金属漆'}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                />
              ) : (
                <>
                  <Space wrap>
                    <Upload
                      multiple
                      accept="image/*"
                      showUploadList={false}
                      beforeUpload={(file, fileList) => {
                        void uploadImages([file as unknown as File], 'primary');
                        void fileList;
                        return Upload.LIST_IGNORE;
                      }}
                    >
                      <Button icon={<UploadOutlined />} loading={uploading} data-testid="mg-image-upload">
                        本地上传参考图
                      </Button>
                    </Upload>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      可多张（取第一张为主图）
                      <HelpTip text={HELP.image} />
                    </Typography.Text>
                  </Space>
                  {imageUrls.length > 0 ? (
                    <Space wrap>
                      {imageUrls.map((u, i) => (
                        <span key={u} style={{ position: 'relative', display: 'inline-block' }}>
                          <img src={u} alt={`ref-${i}`} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, border: i === 0 ? '2px solid #1677ff' : '1px solid #ddd' }} />
                          <Button
                            size="small"
                            type="text"
                            style={{ position: 'absolute', top: -8, right: -8, fontSize: 10 }}
                            onClick={() => setImageUrls((prev) => prev.filter((x) => x !== u))}
                          >
                            ×
                          </Button>
                        </span>
                      ))}
                    </Space>
                  ) : null}
                  <Input
                    placeholder="或粘贴图片地址（https://... 或 /uploads/...）"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    allowClear
                  />
                </>
              )}

              {mode === 'texture' ? (
                <>
                  <Space wrap>
                    <Upload
                      multiple
                      accept="image/*"
                      showUploadList={false}
                      beforeUpload={(file) => {
                        void uploadImages([file as unknown as File], 'reference');
                        return Upload.LIST_IGNORE;
                      }}
                    >
                      <Button size="small" icon={<UploadOutlined />} loading={uploading} data-testid="mg-ref-upload">
                        上传参考照片（可选）
                      </Button>
                    </Upload>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {refUrls.length > 0 ? `已传 ${refUrls.length} 张` : '多张取第一张'}
                      <HelpTip text={HELP.reference} />
                    </Typography.Text>
                  </Space>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    贴图将应用到当前选中模型：{selectedModelName || '（未选中）'}
                  </Typography.Text>
                </>
              ) : (
                <Checkbox checked={autoRetopo} onChange={(e) => setAutoRetopo(e.target.checked)} data-testid="mg-auto-retopo">
                  自动重拓扑
                  <HelpTip text={HELP.autoRetopo} />
                </Checkbox>
              )}

              <Button type="primary" loading={running} disabled={unavailable} onClick={() => void generate()} data-mg-action="generate">
                {running ? '生成中…' : '生成'}
              </Button>

              {running || job ? (
                <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 12, background: '#fafcff' }} data-mg-status={job?.status ?? 'queued'}>
                  {running && job && (job.status === 'queued' || job.status === 'running') ? (
                    <Progress percent={job.status === 'queued' ? 20 : 65} status="active" showInfo={false} style={{ marginBottom: 6 }} />
                  ) : null}
                  <Space size={6} wrap>
                    <Tag color={job?.status === 'succeeded' ? 'green' : job?.status === 'failed' ? 'red' : 'blue'}>{job?.status ?? 'queued'}</Tag>
                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                      链路 {(job?.chain ?? []).join(' → ') || '-'} · provider {job?.provider ?? '-'}
                      {running ? ` · 已等待 ${elapsed}s` : ''}
                    </span>
                    {running && job && (job.status === 'queued' || job.status === 'running') ? (
                      <Button size="small" onClick={() => void cancel()}>
                        取消
                      </Button>
                    ) : null}
                  </Space>
                  {statusText ? (
                    <div style={{ fontSize: 12, color: job?.status === 'queued' && elapsed >= 15 ? '#d46b08' : '#1677ff', marginTop: 6 }}>{statusText}</div>
                  ) : null}
                  {job?.error ? <div style={{ color: '#cf1322', fontSize: 12, marginTop: 6 }}>{job.error}</div> : null}
                  {job?.status === 'succeeded' && mode === 'texture' && textureArtifact ? (
                    <div style={{ marginTop: 10 }}>
                      <Space>
                        <span style={{ display: 'inline-block', width: 20, height: 20, background: color, borderRadius: 4, border: '1px solid #ddd' }} />
                        <span style={{ fontSize: 12 }}>{color}</span>
                        <Button size="small" type="primary" loading={applying} data-mg-action="apply-texture" disabled={!selectedModelId} onClick={() => void applyTexture()}>
                          应用到选中模型
                        </Button>
                      </Space>
                      {!selectedModelId ? <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>请先在场景中选中模型</div> : null}
                    </div>
                  ) : null}
                  {job?.status === 'succeeded' && mode !== 'texture' && glbArtifact ? (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 12, color: '#8c8c8c', wordBreak: 'break-all' }}>{glbArtifact.url}</div>
                      <Button size="small" type="primary" style={{ marginTop: 6 }} loading={applying} data-mg-action="adopt" onClick={() => void adopt()}>
                        采纳到场景
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : null}
        </Space>
      </Drawer>
    </>
  );
}
